import { useState } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  BooleanField,
  TopToolbar,
  EditButton,
  useRecordContext,
  useUpdate,
  useNotify,
  useRefresh,
  ReferenceManyField,
  Datagrid,
  useGetList,
  Loading,
  ArrayField,
  FunctionField,
} from 'react-admin';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Chip,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Block as BlockIcon, CheckCircle as UnblockIcon, LockReset as ResetIcon } from '@mui/icons-material';
import { RoleChip } from '../../components/settings/RoleChip';
import { PasswordResetDialog } from '../../components/settings/PasswordResetDialog';
import { ActionChip } from '../../components/settings/ActionChip';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role_id: number;
  role_name?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

const ActivateButton = () => {
  const record = useRecordContext<AdminUser>();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!record) return null;

  const handleConfirm = () => {
    const newStatus = !record.is_active;

    update(
      'admin-users',
      {
        id: record.id,
        data: { is_active: newStatus },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify(
            newStatus ? 'Admin user activated' : 'Admin user deactivated',
            { type: 'success' }
          );
          refresh();
          setDialogOpen(false);
        },
        onError: (error: any) => {
          notify(`Error: ${error.message}`, { type: 'error' });
          setDialogOpen(false);
        },
      }
    );
  };

  return (
    <>
      <MuiButton
        variant="outlined"
        color={record.is_active ? 'error' : 'success'}
        startIcon={record.is_active ? <BlockIcon /> : <UnblockIcon />}
        onClick={() => setDialogOpen(true)}
        disabled={isLoading}
      >
        {record.is_active ? 'Deactivate' : 'Activate'}
      </MuiButton>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {record.is_active ? 'Deactivate Admin User' : 'Activate Admin User'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {record.is_active ? 'deactivate' : 'activate'}{' '}
            <strong>{record.username}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)}>Cancel</MuiButton>
          <MuiButton
            onClick={handleConfirm}
            color={record.is_active ? 'error' : 'success'}
            variant="contained"
            autoFocus
          >
            {record.is_active ? 'Deactivate' : 'Activate'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ResetPasswordButton = () => {
  const record = useRecordContext<AdminUser>();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  if (!record) return null;

  const handleResetPassword = async () => {
    try {
      // TODO: Call API to reset password
      const mockPassword = `Temp${Math.random().toString(36).slice(-8)}!`;
      setTempPassword(mockPassword);
      setDialogOpen(true);
      notify('Password reset successfully', { type: 'success' });
    } catch (error: any) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <MuiButton
        variant="outlined"
        startIcon={<ResetIcon />}
        onClick={handleResetPassword}
      >
        Reset Password
      </MuiButton>
      <PasswordResetDialog
        open={dialogOpen}
        username={record.username}
        tempPassword={tempPassword}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

const AuditLogSection = () => {
  const record = useRecordContext();
  const { data, isLoading } = useGetList(
    'audit-log',
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'created_at', order: 'DESC' },
      filter: { admin_user_id: record?.id },
    }
  );

  if (isLoading) return <Loading />;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Audit Log (Last 10 Actions)
        </Typography>
        {!data || data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No audit log entries yet.
          </Typography>
        ) : (
          <ArrayField source="data" record={{ data }}>
            <Datagrid bulkActionButtons={false}>
              <TextField source="id" label="ID" />
              <FunctionField
                label="Action"
                render={(record: any) => <ActionChip action={record.action} />}
              />
              <TextField source="resource_type" label="Resource" />
              <TextField source="resource_id" label="Resource ID" />
              <DateField source="created_at" showTime label="Time" />
            </Datagrid>
          </ArrayField>
        )}
      </CardContent>
    </Card>
  );
};

const AdminUserShowActions = () => (
  <TopToolbar>
    <ResetPasswordButton />
    <ActivateButton />
    <EditButton />
  </TopToolbar>
);

const AdminUserDetails = () => {
  const record = useRecordContext<AdminUser>();

  if (!record) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <InfoRow label="ID" value={record.id} />
              <InfoRow label="Username" value={record.username} />
              <InfoRow label="Email" value={record.email} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Role:
                </Typography>
                <RoleChip roleName={record.role_name || 'Unknown'} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                {record.is_active ? (
                  <Chip label="ACTIVE" color="success" />
                ) : (
                  <Chip label="INACTIVE" color="error" />
                )}
              </Box>
              <InfoRow
                label="Created"
                value={
                  record.created_at
                    ? new Date(record.created_at).toLocaleString()
                    : 'N/A'
                }
              />
              <InfoRow
                label="Last Login"
                value={
                  record.last_login
                    ? new Date(record.last_login).toLocaleString()
                    : 'Never'
                }
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <AuditLogSection />
      </Grid>
    </Grid>
  );
};

export const AdminUserShow = () => {
  return (
    <Show actions={<AdminUserShowActions />}>
      <SimpleShowLayout>
        <AdminUserDetails />
      </SimpleShowLayout>
    </Show>
  );
};

const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);
